import cn from 'classnames'
import {AnimatePresence, m} from 'framer-motion'
import _ from 'lodash'
import {FC, PropsWithChildren, useEffect, useRef, useState} from 'react'

type CollapsibleSubmenuProps = {
  isActive: boolean
  // Keeps collapsed children in the DOM, hidden, so their links are in the prerendered HTML.
  keepMounted?: boolean
}

export const CollapsibleSubmenu: FC<PropsWithChildren<CollapsibleSubmenuProps>> = ({
  isActive,
  keepMounted = false,
  children,
}) => {
  const [height, setHeight] = useState(0)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (ref.current) setHeight(ref.current.scrollHeight)
  }, [children, isActive])

  if (keepMounted) {
    // CSS rather than framer, so the prerender already has the right state: collapsing from an
    // initially open state never applied framer's `transitionEnd`, leaving hidden links focusable.
    return (
      <div className={cn('collapsible', {expanded: isActive})}>
        <div className="content">{children}</div>

        <style jsx>{`
          .collapsible {
            display: grid;
            grid-template-rows: 0fr;
            opacity: 0;
            visibility: hidden;
            transition: grid-template-rows 0.2s ease-in, opacity 0.2s ease-in, visibility 0s 0.2s;
          }
          .collapsible.expanded {
            grid-template-rows: 1fr;
            opacity: 1;
            visibility: visible;
            transition: grid-template-rows 0.3s ease-out, opacity 0.3s ease-out;
          }
          .content {
            min-height: 0;
            overflow: hidden;
          }
        `}</style>
      </div>
    )
  }

  return (
    <AnimatePresence>
      {isActive && (
        <m.div
          initial={{opacity: 0, maxHeight: 0}}
          animate={{
            opacity: 1,
            maxHeight: height,
            transition: {duration: 0.3, ease: 'easeOut'},
          }}
          exit={{opacity: 0, maxHeight: 0, transition: {duration: 0.2, ease: 'easeIn'}}}
          onUpdate={_.noop}
        >
          <div ref={ref}>{children}</div>
        </m.div>
      )}
    </AnimatePresence>
  )
}
